import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { App as CapApp } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
import { loadPersistedAuth } from './utils/auth';
import { loginSuccess } from './store/slices/authSlice';

import PrivateRoute from './components/shared/PrivateRoute';
import DirectRecordingStatus from './components/shared/DirectRecordingStatus';
import Layout from './components/shared/Layout';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import CreateRoom from './components/meeting/CreateRoom';
import JoinRoom from './components/meeting/JoinRoom';
import MeetingRoom from './components/meeting/MeetingRoom';
import LiveMeetingObserver from './components/meeting/LiveMeetingObserver';
import Home from './components/meeting/Home';
import LeftMeeting from './components/meeting/LeftMeeting';
import MeetingEnded from './components/meeting/MeetingEnded';
import Rejoin from './components/meeting/Rejoin';
import ConfirmDialog from './components/DialogCustom/ConfirmDialog';
import MobileGate from './components/shared/MobileGate';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { initAndroidChannels } from './notifications/initNotifications';
import { getMyCounters, resetMyCounters } from './utils/api';
import { isNative } from './utils/platform';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { postAppEvent, getAppEventExists } from './utils/appEvents'; // 🔴 FIX: Added getAppEventExists
import { incBadge, clearUpdateBadge } from './notifications/badges';
import { checkForUpdate } from './utils/checkUpdate';
import AlertDialog from './alert/AlertDialog';
import InterviewAutoLogin from './components/meeting/InterviewAutoLogin';
import MeetingAutoLogin from './components/meeting/MeetingAutoLogin';
import ActiveInterviews from './components/meeting/ActiveInterviews';
import {
  disconnectMeetingDashboardSocket,
  refreshMeetingDashboardSocketAuth,
} from './utils/meetingDashboardSocket';
import { installMeetingAudioUnlock } from './utils/meetingJoinSound';

function AppInner() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const isMonitorRoute = routeLocation.pathname === '/monitor-join';

  // Install before any meeting navigation so a normal user click anywhere in
  // the SPA can unlock the local waiting-room notification tone.
  useEffect(() => installMeetingAudioUnlock(), []);

  useEffect(() => {
    if (isMonitorRoute || !isAuthenticated) {
      disconnectMeetingDashboardSocket();
      return undefined;
    }

    const connectDashboardSocket = () => {
      refreshMeetingDashboardSocketAuth();
    };

    connectDashboardSocket();
    window.addEventListener('auth-changed', connectDashboardSocket);

    return () => {
      window.removeEventListener('auth-changed', connectDashboardSocket);
    };
  }, [isAuthenticated, isMonitorRoute]);

  useEffect(() => {
    const handleDeepLink = (event) => {
      try {
        const url = event.detail?.url;
        if (!url) return;

        const urlObj = new URL(url);
        const allowedHosts = new Set([
          'shepherdsmeet.in',
          'www.shepherdsmeet.in',
          'api.shepherdsindia.org',
           'api.shepherdsindia.sbs',
          'www.shepherdsindia.org',
          'shepherdsindia.sbs',
           'shepherdsindia.org',
          'www.shepherdsindia.sbs',
        ]);

        if (!allowedHosts.has(urlObj.hostname)) return;

        if (
          urlObj.pathname === '/interview-join' ||
          urlObj.pathname === '/meeting-join'
        ) {
          // Preserve the signed invite token. Never jump directly to /room.
          navigate(`${urlObj.pathname}${urlObj.search}`, { replace: true });
          return;
        }

        const roomId = urlObj.searchParams.get('roomId');
        if (roomId) {
          navigate(
            `/join-room?roomIdGet=${encodeURIComponent(roomId)}`,
            { replace: true }
          );
        }
      } catch (error) {
        console.error('Deep link error:', error);
      }
    };

    const handleAutoRedirect = (event) => {
      try {
        const { path } = event.detail;
        if (path && path.startsWith('/')) {
          navigate(path);
        }
      } catch (error) {
        console.error('Auto-redirect error:', error);
      }
    };

    window.addEventListener('deepLink', handleDeepLink);
    window.addEventListener('autoRedirect', handleAutoRedirect);

    const checkInitialDeepLink = () => {
      if (window.Capacitor?.Plugins?.App) {
        window.Capacitor.Plugins.App.getLaunchUrl().then(({ url }) => {
          if (url && /shepherds(?:meet\.in|india\.(?:org|sbs))/i.test(url)) {
            window.dispatchEvent(new CustomEvent('deepLink', { detail: { url } }));
          }
        }).catch(console.error);
      }
      
      const urlParams = new URLSearchParams(window.location.search);
      const deepLinkUrl = urlParams.get('deep_link');
      if (deepLinkUrl) {
        window.dispatchEvent(new CustomEvent('deepLink', { detail: { url: deepLinkUrl } }));
      }
    };

    checkInitialDeepLink();

    return () => {
      window.removeEventListener('deepLink', handleDeepLink);
      window.removeEventListener('autoRedirect', handleAutoRedirect);
    };
  }, [navigate]);

  useEffect(() => {
    if (isMonitorRoute) return;
    const cached = loadPersistedAuth();
    if (cached?.user && cached?.token) {
      dispatch(loginSuccess(cached));
    }
  }, [dispatch, isMonitorRoute]);

  useEffect(() => { initAndroidChannels(); }, []);

  useEffect(() => {
    if (!isNative()) return;

    const maybeNotify = async () => {
      try {
        const { update, meta } = await checkForUpdate();
        if (!update || !meta?.apkUrl) return;

        const key = 'shep_last_update_notify';
        const last = Number(localStorage.getItem(key) || 0);
        const now = Date.now();
        if (now - last < 24 * 60 * 60 * 1000) return;

        await LocalNotifications.schedule({
          notifications: [{
            id: 9001,
            title: 'Update available',
            body: `v${meta.versionName || ''} — Tap to install`,
            channelId: 'updates',
            extra: { type: 'update', apkUrl: meta.apkUrl },
            schedule: { at: new Date(Date.now() + 1000) }
          }]
        });

        await incBadge('updates', 1);
        localStorage.setItem(key, String(now));
      } catch {}
    };

    maybeNotify();
    const subP = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) maybeNotify();
    });
    const intervalId = setInterval(maybeNotify, 6 * 60 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
      subP?.then(h => h?.remove?.()).catch(() => {});
    };
  }, []);

  useEffect(() => {
    const handle = LocalNotifications.addListener(
      'localNotificationActionPerformed',
      async (event) => {
        const extra = event?.notification?.extra || {};
        if (extra?.type === 'update' && extra?.apkUrl) {
          if (window.ShepNative?.installFromUrl) {
            try { window.ShepNative.installFromUrl(extra.apkUrl); } catch {}
          } else {
            navigate('/?inapp=1');
          }
          await clearUpdateBadge();
        }
      }
    );
    return () => { handle?.then(h => h?.remove?.()).catch(() => {}); };
  }, [navigate]);

  useEffect(() => {
    const onAppUpdate = async (e) => {
      const s = e?.detail?.state;
      if (s === 'downloading' || s === 'prompt-install' || s === 'needs-permission') {
        const { clearUpdateBadge } = await import('./notifications/badges');
        await clearUpdateBadge();
      }
    };
    window.addEventListener('appUpdate', onAppUpdate);
    return () => window.removeEventListener('appUpdate', onAppUpdate);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!isNative()) return;
        if (Capacitor.getPlatform() !== 'android') return;
        await new Promise(r => setTimeout(r, 500));
        const info = await Device.getInfo();
        const sdk = Number(info?.androidSDKVersion || 0);
        if (sdk < 33) return;
        const perm = await LocalNotifications.checkPermissions();
        if (perm?.display !== 'granted') await LocalNotifications.requestPermissions();
      } catch (e) {
        console.warn('LocalNotifications permission skipped:', e);
      }
    })();
  }, []);

  useEffect(() => {
    let timer;
    (async () => {
      let deviceId = localStorage.getItem('shep_device_id');
      let appVersion = '';
      let platform = 'web';

      try {
        if (isNative()) {
          const idInfo = await Device.getId();
          if (idInfo?.uuid) {
            deviceId = idInfo.uuid;
            localStorage.setItem('shep_device_id', deviceId);
          }
          const info = await Device.getInfo();
          appVersion = info?.appVersion || '';
          platform = info?.platform || 'native';
        } else {
          if (!deviceId) {
            // 🔴 FIX: Safe deviceId generation fallback
            deviceId = (window.crypto && window.crypto.randomUUID) 
              ? window.crypto.randomUUID() 
              : String(Date.now());
            localStorage.setItem('shep_device_id', deviceId);
          }
        }
      } catch {
        if (!deviceId) {
          deviceId = String(Date.now());
          localStorage.setItem('shep_device_id', deviceId);
        }
      }

      const installKey = 'shep_install_reported';
      const alreadyReportedLocally = !!localStorage.getItem(installKey);
      let existsOnServer = false;

      try {
        existsOnServer = alreadyReportedLocally ? true : (await getAppEventExists(deviceId));
      } catch {
        console.warn("Failed to fetch appEvent status");
      }

      if (!existsOnServer && !alreadyReportedLocally) {
        await postAppEvent({ deviceId, event: 'install', appVersion, platform });
        localStorage.setItem(installKey, '1');
      }

      await postAppEvent({ deviceId, event: 'open', appVersion, platform });

      timer = setInterval(() => {
        postAppEvent({ deviceId, event: 'heartbeat', appVersion, platform });
      }, 6 * 60 * 60 * 1000);
    })();

    return () => timer && clearInterval(timer);
  }, []);

  return (
    <>
      {isAuthenticated && !isMonitorRoute && <ActiveInterviews />}
      {isAuthenticated && !isMonitorRoute && <DirectRecordingStatus />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/meeting-ended" element={<MeetingEnded />} />
        <Route path="/rejoin/:roomId" element={<Rejoin />} />
<Route path="/interview-join" element={<InterviewAutoLogin />} />
        <Route path="/meeting-join" element={<MeetingAutoLogin />} />
        <Route path="/monitor-join" element={<LiveMeetingObserver />} />
        <Route element={<PrivateRoute />}>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/create-room" element={<CreateRoom />} />
            <Route path="/join-room" element={<JoinRoom />} />
            <Route path="/left-meeting/:roomId" element={<LeftMeeting />} />
          </Route>
          <Route path="/room/:roomId" element={<MeetingRoom />} />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      <ToastContainer position="top-right" autoClose={3000} />
      <ConfirmDialog />
      <AlertDialog />
    </>
  );
}

export default function App() {
  return (
    // <MobileGate>
      <Router>
        <AppInner />
      </Router>
    // </MobileGate>
  );
}
